import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, X, MapPin, DollarSign, Trash2, AlertCircle, SlidersHorizontal, Phone } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import {
  OPPORTUNITY_TYPES, TYPE_LABEL, TYPE_COLORS,
  postableTypes, applicableTypes, canApply, canPost,
} from "@/lib/permissions";

// Malaysia states & federal territories (shared with Coverage section on landing).
const MALAYSIA_STATES = [
  "Johor", "Kedah", "Kelantan", "Kuala Lumpur", "Labuan", "Melaka",
  "Negeri Sembilan", "Pahang", "Penang", "Perak", "Perlis", "Putrajaya",
  "Sabah", "Sarawak", "Selangor", "Terengganu",
];

const empty = {
  title: "",
  type: "",
  description: "",
  budget: 0,
  currency: "USD",
  location_name: "",
  lat: "",
  lng: "",
  status: "open",
  contact_phone: "",
};

function buildWhatsAppHref(opportunity, globalUrl) {
  const phone = (opportunity.contact_phone || "").replace(/[^\d]/g, "");
  const text = encodeURIComponent(
    `Hi! I'm interested in your opportunity on MIRESCO BIZ:\n"${opportunity.title}"\nCan you share more details?`
  );
  if (phone) return `https://wa.me/${phone}?text=${text}`;
  if (globalUrl) return globalUrl;
  return null;
}

export default function Opportunities() {
  const { user } = useAuth();
  const { t } = useT();
  const isAdmin = !!user?.is_admin;
  const role = user?.role;

  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [minBudget, setMinBudget] = useState("");
  const [maxBudget, setMaxBudget] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [applyOpen, setApplyOpen] = useState(null);
  const [applyMsg, setApplyMsg] = useState("");
  const [globalWA, setGlobalWA] = useState("");

  useEffect(() => {
    api.get("/public/community").then((r) => setGlobalWA(r.data?.whatsapp_url || "")).catch(() => {});
  }, []);

  const myPostable = useMemo(() => postableTypes(role, isAdmin), [role, isAdmin]);
  const myApplicable = useMemo(() => applicableTypes(role, isAdmin), [role, isAdmin]);

  const activeFilterCount = (filterState ? 1 : 0) + (filterStatus ? 1 : 0) + (minBudget ? 1 : 0) + (maxBudget ? 1 : 0);

  const clearAllFilters = () => {
    setFilterType(""); setFilterState(""); setFilterStatus("");
    setMinBudget(""); setMaxBudget(""); setQ(""); setMine(false);
  };

  const load = async () => {
    const params = {};
    if (filterType) params.type = filterType;
    if (filterState) params.state = filterState;
    if (filterStatus) params.status = filterStatus;
    if (minBudget) params.min_budget = Number(minBudget);
    if (maxBudget) params.max_budget = Number(maxBudget);
    if (q) params.q = q;
    if (mine) params.mine = true;
    const { data } = await api.get("/opportunities", { params });
    setItems(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterType, filterState, filterStatus, minBudget, maxBudget, mine]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Read ?type= from URL once on mount so deep-links from landing pages prefill the filter.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get("type");
    if (t && OPPORTUNITY_TYPES.some((o) => o.value === t)) setFilterType(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    if (myPostable.length === 0) {
      toast.error("Your role cannot post opportunities yet.");
      return;
    }
    setEditing(null);
    setForm({ ...empty, type: myPostable[0].value });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...empty, ...it, lat: it.lat ?? "", lng: it.lng ?? "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.type) {
      toast.error("Pick an opportunity type");
      return;
    }
    const payload = {
      ...form,
      budget: Number(form.budget) || 0,
      lat: form.lat === "" ? null : Number(form.lat),
      lng: form.lng === "" ? null : Number(form.lng),
    };
    try {
      if (editing) {
        await api.put(`/opportunities/${editing.opportunity_id}`, payload);
        toast.success("Opportunity updated");
      } else {
        await api.post("/opportunities", payload);
        toast.success("Opportunity posted");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try {
      await api.delete(`/opportunities/${it.opportunity_id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const submitApplication = async () => {
    try {
      await api.post("/applications", {
        opportunity_id: applyOpen.opportunity_id,
        message: applyMsg,
      });
      toast.success("Application submitted");
      setApplyOpen(null);
      setApplyMsg("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const fmtMoney = (v) => `$${Number(v || 0).toLocaleString()}`;

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tag"><span className="w-1.5 h-1.5 bg-[#C8102E]" /> Network</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3">Opportunities</h1>
          <p className="text-zinc-600 text-sm mt-1">
            {myApplicable.length > 0
              ? `You can apply to: ${myApplicable.map((t) => t.short).join(", ")}.`
              : "Sign in or set your role to interact."}
          </p>
        </div>
        {(myPostable.length > 0 || isAdmin) && (
          <button
            onClick={openCreate}
            data-testid="opportunity-create-btn"
            className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2.5 font-semibold transition-colors text-sm"
          >
            <Plus className="w-4 h-4" /> Post opportunity
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilterType("")}
          data-testid="opp-filter-all"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${
            !filterType ? "bg-[#09090b] text-white border-[#09090b]" : "border-zinc-300 hover:bg-zinc-100"
          }`}
        >All</button>
        {OPPORTUNITY_TYPES.map((t) => (
          <button
            key={t.value}
            onClick={() => setFilterType(t.value)}
            data-testid={`opp-filter-${t.value}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${
              filterType === t.value ? "bg-[#09090b] text-white border-[#09090b]" : "border-zinc-300 hover:bg-zinc-100"
            }`}
          >{t.short}</button>
        ))}
        <label className="ml-auto text-xs uppercase tracking-widest font-semibold flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="opp-mine-toggle" />
          Show mine
        </label>
      </div>

      <div className="border border-zinc-200 bg-white">
        <div className="flex items-center gap-2 p-3 border-b border-zinc-200 flex-wrap">
          <Search className="w-4 h-4 text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="opp-search"
            placeholder="Search by title or description…"
            className="flex-1 min-w-[200px] outline-none text-sm bg-transparent"
          />
          <button
            onClick={() => setShowFilters((v) => !v)}
            data-testid="opp-toggle-filters"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${
              showFilters || activeFilterCount > 0 ? "bg-[#C8102E] text-white border-[#C8102E]" : "border-zinc-300 hover:bg-zinc-100"
            }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
            {activeFilterCount > 0 && (
              <span className="bg-white text-[#C8102E] rounded-full px-1.5 text-[10px] ml-0.5">{activeFilterCount}</span>
            )}
          </button>
          {(activeFilterCount > 0 || filterType || q || mine) && (
            <button
              onClick={clearAllFilters}
              data-testid="opp-clear-filters"
              className="text-xs uppercase tracking-widest font-semibold text-zinc-500 hover:text-[#C8102E] px-2"
            >
              Clear all
            </button>
          )}
        </div>

        {showFilters && (
          <div className="p-4 border-b border-zinc-200 bg-zinc-50 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="opp-filters-panel">
            {/* State */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600">State</label>
              <select
                value={filterState}
                onChange={(e) => setFilterState(e.target.value)}
                data-testid="opp-filter-state"
                className="w-full mt-1 px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:border-[#C8102E]"
              >
                <option value="">All states</option>
                {MALAYSIA_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {/* Status */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                data-testid="opp-filter-status"
                className="w-full mt-1 px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:border-[#C8102E]"
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            {/* Min budget */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600">Capital min (RM)</label>
              <input
                type="number"
                min="0"
                value={minBudget}
                onChange={(e) => setMinBudget(e.target.value)}
                data-testid="opp-filter-min-budget"
                placeholder="0"
                className="w-full mt-1 px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:border-[#C8102E]"
              />
            </div>
            {/* Max budget */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600">Capital max (RM)</label>
              <input
                type="number"
                min="0"
                value={maxBudget}
                onChange={(e) => setMaxBudget(e.target.value)}
                data-testid="opp-filter-max-budget"
                placeholder="No limit"
                className="w-full mt-1 px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:border-[#C8102E]"
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {items.length === 0 && (
            <div className="p-10 text-center text-zinc-500 col-span-full">No opportunities found.</div>
          )}
          {items.map((it) => {
            const isOwner = it.owner_id === user?.user_id;
            const eligible = canApply(role, it.type, isAdmin);
            return (
              <div key={it.opportunity_id} className="border-b border-r border-zinc-200 p-5 hover:bg-zinc-50 transition-colors flex flex-col" data-testid={`opp-card-${it.opportunity_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 ${TYPE_COLORS[it.type] || "border-zinc-300"}`}>
                    {TYPE_LABEL[it.type] || it.type}
                  </span>
                  <span className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 ${
                    it.status === "open" ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-zinc-200 text-zinc-500 bg-zinc-50"
                  }`}>{it.status}</span>
                </div>
                <h3 className="font-display font-black text-lg tracking-tight mt-2">{it.title}</h3>
                <p className="text-sm text-zinc-600 mt-1 line-clamp-3">{it.description || "—"}</p>

                <div className="mt-3 text-xs text-zinc-600 space-y-1">
                  {Number(it.budget) > 0 && (
                    <div className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> {fmtMoney(it.budget)} {it.currency}</div>
                  )}
                  {it.location_name && (
                    <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {it.location_name}</div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-xs gap-2">
                  <span className="text-zinc-500 font-mono truncate">by {it.owner_name || "—"}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {(() => {
                      const waHref = buildWhatsAppHref(it, globalWA);
                      if (!waHref || isOwner) return null;
                      return (
                        <a
                          href={waHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`opp-whatsapp-${it.opportunity_id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-white text-xs font-semibold hover:opacity-90"
                          style={{ background: "#25D366" }}
                        >
                          <Phone className="w-3 h-3" /> {t("opps.whatsapp")}
                        </a>
                      );
                    })()}
                    {isOwner ? (
                      <>
                        <button onClick={() => openEdit(it)} data-testid={`opp-edit-${it.opportunity_id}`} className="px-2 py-1 hover:bg-zinc-100 text-xs font-semibold">Edit</button>
                        <button onClick={() => remove(it)} data-testid={`opp-delete-${it.opportunity_id}`} className="px-2 py-1 hover:bg-red-50 text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : eligible ? (
                      <button onClick={() => setApplyOpen(it)} data-testid={`opp-apply-${it.opportunity_id}`} className="px-3 py-1.5 bg-[#C8102E] hover:bg-[#9A0C24] text-white text-xs font-semibold">
                        Apply
                      </button>
                    ) : (
                      <span className="text-[10px] text-zinc-400 uppercase tracking-widest font-semibold" data-testid={`opp-ineligible-${it.opportunity_id}`}>
                        Role n/a
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="opp-dialog">
          <form onSubmit={save} className="bg-white border border-zinc-300 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-5 border-b border-zinc-200 sticky top-0 bg-white">
              <h2 className="font-display font-black text-xl tracking-tight">{editing ? "Edit opportunity" : "Post opportunity"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="p-1" data-testid="opp-dialog-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <Field label="Title" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="opp-field-title" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-zinc-500">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    data-testid="opp-field-type"
                    className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E] bg-white"
                  >
                    <option value="">Select…</option>
                    {(isAdmin ? OPPORTUNITY_TYPES : myPostable).map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="overline text-zinc-500">Status</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="opp-field-status" className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E] bg-white">
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
              {form.type && (
                <div className="text-xs text-zinc-500 border border-zinc-200 p-3 flex items-start gap-2" data-testid="opp-type-hint">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-[#C8102E] shrink-0" />
                  <span>
                    <strong>{TYPE_LABEL[form.type]}</strong> opportunities are applied to by{" "}
                    {(applicableTypes(null, false).find((x) => x.value === form.type) ?
                      [] : [])}{/* dummy */}
                    <strong>
                      {(APPLICATION_PERMISSIONS_DISPLAY(form.type) || []).join(", ")}
                    </strong>.
                  </span>
                </div>
              )}
              <div>
                <label className="overline text-zinc-500">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  data-testid="opp-field-description"
                  className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Budget" type="number" value={form.budget} onChange={(v) => setForm({ ...form, budget: v })} testid="opp-field-budget" />
                <Field label="Currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} testid="opp-field-currency" />
              </div>
              <Field label="Location (city, country)" value={form.location_name} onChange={(v) => setForm({ ...form, location_name: v })} testid="opp-field-location" />
              <Field
                label="WhatsApp contact (with country code, e.g. 60123456789)"
                value={form.contact_phone}
                onChange={(v) => setForm({ ...form, contact_phone: v })}
                testid="opp-field-contact-phone"
              />
            </div>
            <div className="p-5 border-t border-zinc-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 hover:bg-zinc-100 text-sm">Cancel</button>
              <button type="submit" data-testid="opp-save-btn" className="px-4 py-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white font-semibold text-sm">
                {editing ? "Save changes" : "Post"}
              </button>
            </div>
          </form>
        </div>
      )}

      {applyOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="apply-dialog">
          <div className="bg-white border border-zinc-300 w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <h2 className="font-display font-black text-xl tracking-tight">Apply to "{applyOpen.title}"</h2>
              <button onClick={() => setApplyOpen(null)} className="p-1" data-testid="apply-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <label className="overline text-zinc-500">Your message</label>
              <textarea
                value={applyMsg}
                onChange={(e) => setApplyMsg(e.target.value)}
                rows={5}
                data-testid="apply-message"
                className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]"
                placeholder="Why you're a good fit…"
              />
            </div>
            <div className="p-5 border-t border-zinc-200 flex justify-end gap-2">
              <button onClick={() => setApplyOpen(null)} className="px-4 py-2 border border-zinc-300 hover:bg-zinc-100 text-sm">Cancel</button>
              <button onClick={submitApplication} data-testid="apply-submit" className="px-4 py-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white font-semibold text-sm">
                Submit application
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// helper for the small permission hint
function APPLICATION_PERMISSIONS_DISPLAY(type) {
  // Lazy import to avoid cyclic ref; mirrors permissions.js
  const map = {
    agent: ["Entrepreneurs"], affiliate: ["Entrepreneurs"], stockist: ["Entrepreneurs"],
    distributor: ["Entrepreneurs"], kiosk: ["Entrepreneurs"], vendor: ["Entrepreneurs"],
    influencer_campaign: ["Influencers"], contractor_job: ["Contractors"],
    investment: ["Investors"], partnership: ["Business Owners", "Investors"],
  };
  return map[type];
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="overline text-zinc-500">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]"
      />
    </div>
  );
}
